import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import { EventTypes } from "./eventManager.js";
import { eventManager } from "./eventManager.js";
import ChatService from "@/src/api/ChatService.js";
import { playMessageNotification, playCallEndSound, playCallingSound, stopCallingSound } from "@/src/utils/audioNotification.js";
import { chatTypes, chatRecipientTypes, chatToastTypes } from "@/src/constants/chatConstants.js";

/**
 * Checks if the current user is a member of the given group.
 * DMs, self-DMs, group-DMs, and private groups imply membership if they appear in the store.
 * Public groups require explicit membership confirmation via the membership cache.
 */
function isUserGroupMember(groupId, currentUserId) {
  if (!groupId || !currentUserId) return false;

  const groups = store.getters["chats/groups"] || [];
  const group = groups.find((g) => String(g.id) === String(groupId));

  if (!group) return false;

  // The API returns is_member field — use it as the primary check
  if (group.is_member === true) return true;

  // DMs/private imply membership if they appear in the store
  if (group.is_dm || group.type === "dm" || group.type === "self_dm" || group.type === "group_dm" || group.type === "private") {
    return true;
  }

  // For public groups or when is_member is false/undefined, check memberGroupIds
  const memberGroupIds = store.getters["huddles/memberGroupIds"] || [];
  return memberGroupIds.includes(String(groupId));
}

async function handleNewMessage(event) {
  const { group_id, message_id, sender_id, thread_id } = event;
  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeChat = store.getters["chats/activeChat"];
  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const activeThreadId = store.getters["chats/threadId"];

  const activeChatId = String(activeChat?.id).replace(/^group_/, '');
  const eventGroupId = String(group_id);

  if (isOnChatsPage && activeChatId === eventGroupId) {
    const pendingMessages = store.getters["chats/pendingMessages"];
    if (pendingMessages.has(message_id)) {
      return;
    }

    const isRootMessage = thread_id === message_id;

    try {
      const response = await ChatService.fetchMessageById(message_id, group_id);

      if (response?.success && response?.message) {
        // Re-check active chat after async fetch — user may have switched chats
        const currentActiveChat = store.getters["chats/activeChat"];
        const currentActiveChatId = String(currentActiveChat?.id).replace(/^group_/, '');
        if (currentActiveChatId !== eventGroupId) {
          return;
        }

        const isBroadcastFromThread = response.message.broadcast_from_thread_id;
        const messageSenderId = response.message.user_id || response.message.sender?.id;
        const isOwnMessage = messageSenderId && currentUserId &&
          (Number(messageSenderId) === Number(currentUserId) || String(messageSenderId) === String(currentUserId));

        if (isOwnMessage && !isBroadcastFromThread) {
          return;
        }

        if (response.message.broadcast_from_thread_id) {
          const threadMessages = store.getters["chats/threadMessages"] || [];
          const mainMessages = store.getters["chats/messages"] || [];

          let replyMsg = threadMessages.find(m => m.id === response.message.broadcast_from_thread_id);

          if (!replyMsg) {
            replyMsg = mainMessages.find(m => m.id === response.message.broadcast_from_thread_id);
          }

          if (replyMsg && replyMsg.thread_id) {
            response.message._resolvedThreadRoot = replyMsg.thread_id;
          } else if (!replyMsg) {
            try {
              const replyResponse = await ChatService.fetchMessageById(response.message.broadcast_from_thread_id, group_id);
              if (replyResponse?.success && replyResponse?.message && replyResponse.message.thread_id) {
                response.message._resolvedThreadRoot = replyResponse.message.thread_id;
              }
            } catch (err) {
              console.error('Failed to fetch reply message:', err);
            }
          }
        }

        await store.dispatch("chats/addMessage", response.message);

        const isOneOnOneDM = currentActiveChat?.type === "dm" || currentActiveChat?.type === "self_dm";
        if (isOwnMessage || isOneOnOneDM) {
          eventBus.emit("scroll-to-bottom-dm");
        }
      }
    } catch (err) {
      console.error("Failed to fetch new message:", err);
    }
  }
}

function findMatchingEmbeddedChatId(groupId) {
  if (!groupId) return null;
  const openChatIds = store.getters["embeddedChats/openChatIds"] || [];
  const eventGroupId = String(groupId).replace(/^group_/, '');
  return openChatIds.find((id) => String(id).replace(/^group_/, '') === eventGroupId) || null;
}

async function handleMessageEdited(event) {
  const { group_id, message_id } = event;
  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeChat = store.getters["chats/activeChat"];
  const activeChatId = String(activeChat?.id).replace(/^group_/, '');
  const eventGroupId = String(group_id);
  const matchedChatId = findMatchingEmbeddedChatId(group_id);

  const needsMainUpdate = isOnChatsPage && activeChatId === eventGroupId;
  if (!needsMainUpdate && !matchedChatId) return;

  try {
    const response = await ChatService.fetchMessageById(message_id, group_id);
    if (response?.success && response?.message) {
      if (needsMainUpdate) {
        store.dispatch("chats/updateMessage", response.message);
      }
      if (matchedChatId) {
        store.commit("embeddedChats/UPDATE_MESSAGE", { chatId: matchedChatId, message: response.message });
      }
    }
  } catch (err) {
    console.error("Failed to fetch edited message:", err);
  }
}

function handleMessageDeleted(event) {
  const { message_id, group_id } = event;
  store.dispatch("chats/removeMessage", message_id);

  // Sync deletion to embedded chats
  const openChatIds = store.getters["embeddedChats/openChatIds"] || [];
  if (group_id) {
    const eventGroupId = String(group_id).replace(/^group_/, '');
    const matchedChatId = openChatIds.find(
      (id) => String(id).replace(/^group_/, '') === eventGroupId
    );
    if (matchedChatId) {
      store.commit("embeddedChats/DELETE_MESSAGE", { chatId: matchedChatId, messageId: message_id });
    }
  }
}

const _seenReactionEventIds = new Set();

async function handleReactionUpdated(event) {
  const { message_id, emoji_key, action, group_id, user_id, event_id } = event;

  // Deduplicate — the event can arrive from multiple subscriptions (group + wildcard).
  // Always build a key: prefer event_id, fall back to a composite of stable fields.
  const dedupKey = event_id || `${message_id}_${emoji_key}_${user_id}_${action}`;
  if (_seenReactionEventIds.has(dedupKey)) return;
  _seenReactionEventIds.add(dedupKey);
  setTimeout(() => _seenReactionEventIds.delete(dedupKey), 10000);

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isOwnReaction = user_id && currentUserId &&
    (Number(user_id) === Number(currentUserId) || String(user_id) === String(currentUserId));

  if (!isOwnReaction) {
    const delta = action === "add" ? 1 : -1;
    store.dispatch("chats/updateMessageReactions", {
      messageId: message_id,
      emoji: emoji_key,
      increment: delta,
    });
    store.commit("embeddedChats/UPDATE_REACTION", {
      messageId: message_id,
      emoji: emoji_key,
      delta,
    });
  }

  if (action !== "add" || !group_id || !user_id) return;
  if (isOwnReaction) return;

  // Skip notifications for self-DM conversations
  const allGroups = store.getters["chats/groups"] || [];
  const reactionGroup = allGroups.find(g => {
    const gId = String(g.id).replace(/^group_/, '');
    return gId === String(group_id).replace(/^group_/, '');
  });
  if (reactionGroup?.type === chatTypes.SELF_DM) return;

  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeChat = store.getters["chats/activeChat"];
  const activeChatId = String(activeChat?.id).replace(/^group_/, '');
  const eventGroupId = String(group_id).replace(/^group_/, '');
  if (isOnChatsPage && activeChatId === eventGroupId) return;

  try {
    const response = await ChatService.fetchMessageById(message_id, group_id);
    if (response?.success && response?.message) {
      const msg = response.message;
      const msgOwnerId = msg.user_id || msg.sender?.id;
      const isOwnMessage = msgOwnerId && currentUserId &&
        (Number(msgOwnerId) === Number(currentUserId) || String(msgOwnerId) === String(currentUserId));
      if (!isOwnMessage) return;

      let reactorName = null;
      let reactorAvatarUrl = null;
      let reactorAvatarColor = null;
      const allUsers = store.getters["users/users"] || [];
      const reactor = allUsers.find(u =>
        Number(u.id) === Number(user_id) || String(u.id) === String(user_id)
      );
      if (reactor) {
        reactorName = reactor.name
          || `${reactor.first_name || ''} ${reactor.last_name || ''}`.trim()
          || reactor.email
          || null;
        reactorAvatarUrl = reactor.avatar_url || reactor.profile_pic || null;
        reactorAvatarColor = reactor.avatar_color || null;
      }

      if (store.getters["chats/chatNotificationSoundEnabled"]) {
        playMessageNotification();
      }

      if (!isOnChatsPage) {
        store.dispatch("chats/setUnreadIndicator", true);
      }

      eventBus.emit("chat-toast-message", {
        type: chatToastTypes.REACTION,
        groupId: group_id,
        messageId: `reaction_${message_id}_${emoji_key}_${user_id}`,
        senderName: reactorName || "Someone",
        senderAvatarUrl: reactorAvatarUrl,
        senderAvatarColor: reactorAvatarColor,
        emoji: emoji_key,
        messagePreview: msg.text || "",
      });
    }
  } catch (err) {
    console.error("Failed to fetch message for reaction toast:", err);
  }
}

function handleThreadCountUpdated(event) {
  const { thread_id, reply_count } = event;
  store.dispatch("chats/updateThreadCount", {
    threadId: thread_id,
    replyCount: reply_count,
  });

  // Sync to embedded chats - set absolute count by calculating delta
  const openChats = store.getters["embeddedChats/openChats"] || [];
  openChats.forEach((entry) => {
    const msg = entry.messages.find((m) => String(m.id) === String(thread_id));
    if (msg) {
      const currentCount = msg.reply_count || 0;
      const delta = reply_count - currentCount;
      if (delta !== 0) {
        store.commit("embeddedChats/UPDATE_REPLY_COUNT", {
          chatId: entry.chat?.id,
          threadId: thread_id,
          delta,
        });
      }
    }
  });
}

async function handleNewThreadReply(event) {
  const { group_id, thread_id, message_id, user_id: sender_id } = event;
  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeThreadId = store.getters["chats/threadId"];
  const activeThreadMessageID = store.getters["chats/threadMessageID"];
  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");

  const isOwnMessage = sender_id && currentUserId &&
    (Number(sender_id) === Number(currentUserId) || String(sender_id) === String(currentUserId));

  const isActiveThread = thread_id && (
    (activeThreadId && String(activeThreadId) === String(thread_id)) ||
    (activeThreadMessageID && String(activeThreadMessageID) === String(thread_id))
  );

  if (isOnChatsPage && isActiveThread) {
    if (!sender_id || isOwnMessage) return;

    const pendingMessages = store.getters["chats/pendingMessages"];
    if (pendingMessages.has(message_id)) return;

    try {
      const response = await ChatService.fetchMessageById(message_id, group_id);
      if (response?.success && response?.message) {
        await store.dispatch("chats/addThreadMessage", response.message);
      }
    } catch (err) {
      console.error("Failed to fetch new thread reply:", err);
    }
  }

  // Sync to embedded chats if thread is open
  if (!isOwnMessage && !isOnChatsPage) {
    const matchedChatId = findMatchingEmbeddedChatId(group_id);
    if (matchedChatId) {
      const entry = (store.getters["embeddedChats/openChats"] || []).find(
        (c) => String(c.chat?.id).replace(/^group_/, '') === String(group_id).replace(/^group_/, '')
      );
      if (entry?.threadOpen && String(entry.threadId) === String(thread_id)) {
        try {
          const response = await ChatService.fetchMessageById(message_id, group_id);
          if (response?.success && response?.message) {
            store.commit("embeddedChats/ADD_THREAD_MESSAGE", { chatId: matchedChatId, message: response.message });
          }
        } catch (err) {
          console.error("Failed to sync thread reply to embedded chats:", err);
        }
      }
    }
  }
}

async function handleThreadReplyEdited(event) {
  const { group_id, thread_id, message_id } = event;
  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeThreadId = store.getters["chats/threadId"];
  const activeThreadMessageID = store.getters["chats/threadMessageID"];
  const matchedChatId = findMatchingEmbeddedChatId(group_id);

  const isActiveThread = thread_id && (
    (activeThreadId && String(activeThreadId) === String(thread_id)) ||
    (activeThreadMessageID && String(activeThreadMessageID) === String(thread_id))
  );

  const needsMainUpdate = isOnChatsPage && isActiveThread;
  if (!needsMainUpdate && !matchedChatId) return;

  try {
    const response = await ChatService.fetchMessageById(message_id, group_id);
    if (response?.success && response?.message) {
      if (needsMainUpdate) {
        store.dispatch("chats/updateThreadMessage", response.message);

        if (response.message.also_sent_to_channel) {
          store.dispatch("chats/updateBroadcastCopy", {
            threadReplyId: message_id,
            text: response.message.text,
            edited: response.message.edited,
            edited_at: response.message.edited_at,
            attachments: response.message.attachments,
          });
        }
      }
      if (matchedChatId) {
        store.commit("embeddedChats/UPDATE_MESSAGE", { chatId: matchedChatId, message: response.message });
      }
    }
  } catch (err) {
    console.error("Failed to fetch edited thread reply:", err);
  }
}

function handleThreadReplyDeleted(event) {
  const { message_id } = event;
  store.dispatch("chats/removeThreadMessage", message_id);

  // Sync to embedded chats
  const openChats = store.getters["embeddedChats/openChats"] || [];
  openChats.forEach((entry) => {
    if (entry.threadMessages.some((m) => String(m.id) === String(message_id))) {
      store.commit("embeddedChats/DELETE_MESSAGE", { chatId: entry.chat?.id, messageId: message_id });
    }
  });
}

const _seenThreadReactionEventIds = new Set();

function handleThreadReactionUpdated(event) {
  const { message_id, emoji_key, action, thread_id, user_id, event_id } = event;

  const dedupKey = event_id || `${thread_id}_${message_id}_${emoji_key}_${user_id}_${action}`;
  if (_seenThreadReactionEventIds.has(dedupKey)) return;
  _seenThreadReactionEventIds.add(dedupKey);
  setTimeout(() => _seenThreadReactionEventIds.delete(dedupKey), 10000);

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isOwnReaction = user_id && currentUserId &&
    (Number(user_id) === Number(currentUserId) || String(user_id) === String(currentUserId));

  const delta = action === "add" ? 1 : -1;
  store.dispatch("chats/updateThreadMessageReactions", {
    messageId: message_id,
    emojiKey: emoji_key,
    delta,
  });

  // Sync to embedded chats
  if (!isOwnReaction) {
    store.commit("embeddedChats/UPDATE_REACTION", {
      messageId: message_id,
      emoji: emoji_key,
      delta,
    });
  }
}

function handleTyping(event) {
  const { group_id, user_id, is_typing } = event;
  
  if (is_typing) {
    store.dispatch("chats/setTypingUser", { groupId: group_id, userId: user_id });
  } else {
    store.commit("chats/CLEAR_TYPING_USER", group_id);
  }
}

const _sidebarSeenMessageIds = new Set();

async function handleSidebarNewMessage(event) {
  const { group_id, message_id, message_preview, sender_id } = event;

  if (message_id && _sidebarSeenMessageIds.has(message_id)) return;
  if (message_id) {
    _sidebarSeenMessageIds.add(message_id);
    setTimeout(() => _sidebarSeenMessageIds.delete(message_id), 10000);
  }

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");

  const currentRoute = window.location.pathname;
  const isOnChatsPage = currentRoute.includes('/projects/chats');
  const activeChat = store.getters["chats/activeChat"];
  const activeChatId = String(activeChat?.id).replace(/^group_/, '');
  const eventGroupId = String(group_id).replace(/^group_/, '');
  const isActiveChat = isOnChatsPage && activeChatId === eventGroupId;
  const isOwnMessage = sender_id && currentUserId && (Number(sender_id) === Number(currentUserId) || String(sender_id) === String(currentUserId));

  // Never generate notifications for self-DM conversations
  const groups = store.getters["chats/groups"] || [];
  const matchedGroupForSelfDM = groups.find(g => {
    const gId = String(g.id).replace(/^group_/, '');
    return gId === eventGroupId;
  });
  const isSelfDMGroup = matchedGroupForSelfDM?.type === chatTypes.SELF_DM;

  let displayMessage = message_preview;
  let attachments = null;
  let senderName = null;
  let senderAvatarUrl = null;
  let senderAvatarColor = null;
  let isMention = false;
  let isThreadReply = false;
  let threadRootId = null;
  let isSystemMsg = false;
  const needsToast = !isOwnMessage && !isSelfDMGroup && sender_id && !isOnChatsPage;
  const needsSoundCheck = !isOwnMessage && !isSelfDMGroup && sender_id;

  if (message_id && (!message_preview || !message_preview.trim() || needsToast || needsSoundCheck)) {
    try {
      const response = await ChatService.fetchMessageById(message_id, group_id);
      if (response?.success && response?.message) {
        const msg = response.message;
        if (msg.attachments && msg.attachments.length > 0) {
          attachments = msg.attachments;
          if (!displayMessage || !displayMessage.trim()) {
            const fileName = msg.attachments[0]?.file_name || msg.attachments[0]?.name;
            displayMessage = fileName ? `📎 ${fileName}` : "📎 Attachment";
          }
        }
        if (!displayMessage && msg.text) {
          displayMessage = msg.text;
        }
        if (msg.is_system) {
          isSystemMsg = true;
        }
        if (needsSoundCheck) {
          if (msg.mentioned_users?.length > 0) {
            isMention = msg.mentioned_users.some(u =>
              Number(u.id) === Number(currentUserId) || String(u.id) === String(currentUserId)
            );
          }
          const msgThreadId = msg.thread_id;
          if (msgThreadId && String(msgThreadId) !== String(message_id) && !isMention) {
            try {
              const threadRootResponse = await ChatService.fetchMessageById(msgThreadId, group_id);
              if (threadRootResponse?.success && threadRootResponse?.message) {
                const rootMsg = threadRootResponse.message;
                const rootOwnerId = rootMsg.user_id || rootMsg.sender?.id;
                if (rootOwnerId && currentUserId &&
                  (Number(rootOwnerId) === Number(currentUserId) || String(rootOwnerId) === String(currentUserId))) {
                  isThreadReply = true;
                  threadRootId = msgThreadId;
                }
              }
            } catch (err) {
              console.error("Failed to fetch thread root message:", err);
            }
          }
        }
        if (needsToast) {
          if (msg.sender) {
            const sender = msg.sender;
            senderName = sender.name
              || `${sender.first_name || ''} ${sender.last_name || ''}`.trim()
              || sender.email
              || null;
            senderAvatarUrl = sender.avatar_url || sender.profile_pic || null;
            senderAvatarColor = sender.avatar_color || null;
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch message for attachment check:", err);
    }
  }

  const matchedGroup = groups.find(g => {
    const gId = String(g.id).replace(/^group_/, '');
    const targetId = String(group_id).replace(/^group_/, '');
    return gId === targetId;
  });
  const groupExists = !!matchedGroup;

  if (!isOwnMessage && sender_id) {
    const isDMGroup = matchedGroup?.is_dm === true || matchedGroup?.type === chatTypes.DM || matchedGroup?.type === chatTypes.SELF_DM || matchedGroup?.type === chatTypes.GROUP_DM;
    const isPublicGroup = !isDMGroup && matchedGroup?.type === chatTypes.PUBLIC;
    let isNonMember = false;

    if (isPublicGroup) {
      const membershipCache = store.getters["chats/groupMembershipCache"] || {};
      const cachedMembership = membershipCache[eventGroupId];
      if (cachedMembership === false) {
        isNonMember = true;
      } else if (cachedMembership === undefined && isActiveChat) {
        isNonMember = !store.getters["chats/isCurrentUserGroupMember"];
      }
    }

    const notificationSoundEnabled = store.getters["chats/chatNotificationSoundEnabled"];
    const shouldPlaySound = notificationSoundEnabled && !isSystemMsg && !isNonMember && (isDMGroup || isMention || isThreadReply);
    if (shouldPlaySound) {
      playMessageNotification();
    }

    if (!isOnChatsPage && !isNonMember) {
      store.dispatch("chats/setUnreadIndicator", true);

      let groupName = null;
      if (isMention) {
        let groupForName = matchedGroup;
        if (!groupForName) {
          try {
            const groupResponse = await ChatService.fetchGroupByID(group_id);
            if (groupResponse?.success && groupResponse?.group) {
              groupForName = groupResponse.group;
            }
          } catch (err) {
            console.error("Failed to fetch group for mention toast:", err);
          }
        }
        if (groupForName) {
          const isDMForName = groupForName.is_dm === true || groupForName.type === chatTypes.DM || groupForName.type === chatTypes.SELF_DM || groupForName.type === chatTypes.GROUP_DM;
          if (!isDMForName && groupForName.name) {
            groupName = groupForName.name;
          }
        }
      }

      const toastType = isMention ? chatToastTypes.MENTION : isThreadReply ? chatToastTypes.THREAD_REPLY : chatToastTypes.MESSAGE;
      eventBus.emit("chat-toast-message", {
        type: toastType,
        groupId: group_id,
        messageId: message_id,
        senderName: senderName || "Someone",
        senderAvatarUrl: senderAvatarUrl,
        senderAvatarColor: senderAvatarColor,
        messagePreview: displayMessage || "",
        groupName: groupName,
        threadRootId: threadRootId,
      });
    }
  }

  if (!groupExists) {
    try {
      const groupResponse = await ChatService.fetchGroupByID(group_id);
      
      if (groupResponse?.success && groupResponse?.group) {
        const fetchedGroup = groupResponse.group;
        
        const isKnownChannelType = fetchedGroup.type === chatTypes.PUBLIC || fetchedGroup.type === chatTypes.PRIVATE || fetchedGroup.type === chatTypes.EXTERNAL;

        if (fetchedGroup.type === chatTypes.GROUP_DM) {
          fetchedGroup.is_dm = true;
          if (!fetchedGroup.recipient_type) {
            fetchedGroup.recipient_type = chatRecipientTypes.GROUP_DM;
          }
        } else if (fetchedGroup.type === chatTypes.DM) {
          fetchedGroup.is_dm = true;
          if (!fetchedGroup.recipient_type) {
            fetchedGroup.recipient_type = chatRecipientTypes.ONE_ON_ONE;
          }
        } else if (fetchedGroup.type === chatTypes.SELF_DM) {
          fetchedGroup.is_dm = true;
          if (!fetchedGroup.recipient_type) {
            fetchedGroup.recipient_type = chatRecipientTypes.ONE_ON_ONE;
          }
        }

        const needsDMClassification = !isKnownChannelType && !fetchedGroup.is_dm;
        if (needsDMClassification) {
          try {
            const membersResponse = await ChatService.fetchGroupMembers(group_id);
            if (membersResponse?.success && membersResponse?.members) {
              const memberCount = membersResponse.members.length;
              const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
              const allSameUser = memberCount > 0 && membersResponse.members.every(
                m => Number(m.user_id) === Number(currentUserId)
              );

              if (allSameUser) {
                fetchedGroup.is_dm = true;
                fetchedGroup.type = chatTypes.SELF_DM;
                fetchedGroup.recipient_type = chatRecipientTypes.ONE_ON_ONE;
              } else if (memberCount === 2) {
                fetchedGroup.is_dm = true;
                fetchedGroup.type = chatTypes.DM;
                fetchedGroup.recipient_type = chatRecipientTypes.ONE_ON_ONE;
              } else if (memberCount > 2) {
                fetchedGroup.is_dm = true;
                fetchedGroup.type = chatTypes.GROUP_DM;
                fetchedGroup.recipient_type = chatRecipientTypes.GROUP_DM;
              }
            }
          } catch (err) {
            console.error("Failed to fetch members to determine DM type:", err);
          }
        }
        
        if (isActiveChat) {
          fetchedGroup.unread_count = 0;
        } else {
          fetchedGroup.unread_count = 1;
        }
        
        await store.dispatch("chats/addGroupToList", fetchedGroup);
        
        await store.dispatch("chats/updateSidebarGroup", {
          groupId: group_id,
          lastMessage: displayMessage,
          lastMessageAttachments: attachments,
          senderId: sender_id,
        });
        
        eventBus.emit("new-group-added-to-list", group_id);
        
        if (isActiveChat && message_id) {
          try {
            await ChatService.markAsRead(group_id);
          } catch (markError) {
            console.error("Failed to mark new group as read:", markError);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch group for new conversation:", err);
    }
    
    return;
  }
  
  store.dispatch("chats/updateSidebarGroup", {
    groupId: group_id,
    lastMessage: displayMessage,
    lastMessageAttachments: attachments,
    senderId: sender_id,
  });

  if (isActiveChat && message_id && !isOwnMessage) {
    try {
      const response = await ChatService.fetchMessageById(message_id, group_id);
      if (response?.success && response?.message) {
        // Re-check active chat after async fetch — user may have switched chats
        const currentActiveChat = store.getters["chats/activeChat"];
        const currentActiveChatId = String(currentActiveChat?.id).replace(/^group_/, '');
        if (currentActiveChatId !== eventGroupId) {
          return;
        }

        await store.dispatch("chats/addMessage", response.message);

        try {
          await ChatService.markAsRead(group_id);
        } catch (markError) {
          console.error("Failed to auto-mark messages as read:", markError);
        }

        const groups = store.getters["chats/groups"];
        const group = groups.find(g => {
          const gId = String(g.id).replace(/^group_/, '');
          const targetId = String(group_id).replace(/^group_/, '');
          return gId === targetId;
        });

        const isOneOnOneDM = group?.type === "dm" || group?.type === "self_dm";
        if (isOneOnOneDM) {
          eventBus.emit("scroll-to-bottom-dm");
        }
      }
    } catch (err) {
      console.error("Failed to fetch message from sidebar event:", err);
    }
  }
}

function handleGroupUpdated(event) {
  const { group_id, name, description, avatar_url, profile_pic, type } = event;
  store.dispatch("chats/updateGroupInfo", {
    groupId: group_id,
    name,
    description,
    avatarUrl: avatar_url,
    profilePic: profile_pic,
  });

  // Sync to embedded chats
  const matchedChatId = findMatchingEmbeddedChatId(group_id);
  if (matchedChatId) {
    const updates = {};
    if (name !== undefined) updates.name = name;
    if (description !== undefined) updates.description = description;
    if (avatar_url !== undefined) updates.avatar_url = avatar_url;
    if (profile_pic !== undefined) updates.profile_pic = profile_pic;
    if (type !== undefined) updates.type = type;
    if (Object.keys(updates).length > 0) {
      store.commit("embeddedChats/UPDATE_CHAT_INFO", { chatId: matchedChatId, updates });
    }
  }
}

function handleAddedToGroup(event) {
  const { group_id } = event;
  eventBus.emit("group-created");
  if (group_id) {
    // Add to confirmed member groups (wildcard topics cover all groups)
    const currentMemberIds = store.getters["huddles/memberGroupIds"] || [];
    if (!currentMemberIds.includes(String(group_id))) {
      store.commit("huddles/SET_MEMBER_GROUP_IDS", [...currentMemberIds, String(group_id)]);
    }
  }
}

function handleRemovedFromGroup(event) {
  const { group_id } = event;
  store.dispatch("chats/removeGroup", group_id);

  // Close embedded chat if open
  const matchedChatId = findMatchingEmbeddedChatId(group_id);
  if (matchedChatId) {
    store.dispatch("embeddedChats/closeChat", matchedChatId);
  }

  if (group_id) {
    const currentMemberIds = store.getters["huddles/memberGroupIds"] || [];
    const filtered = currentMemberIds.filter((id) => String(id) !== String(group_id));
    if (filtered.length !== currentMemberIds.length) {
      store.commit("huddles/SET_MEMBER_GROUP_IDS", filtered);
    }
  }
}

function handleUnreadUpdate(event) {
  const { group_id, unread_count } = event;
  store.dispatch("chats/updateUnreadCount", {
    groupId: group_id,
    unreadCount: unread_count,
  });
}

function handleChatSidebarEvent(message) {
  if (message && message.event) {
    eventManager.dispatch(message);
  }
}

function closeHuddleBrowserNotification(groupId) {
  // Browser Notification API doesn't provide a way to close by tag programmatically,
  // but using the same tag ensures new notifications replace old ones.
  // We close by creating a silent notification with the same tag and immediately closing it.
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    const n = new Notification("", { tag: `huddle-invite-${groupId}`, silent: true });
    n.close();
  } catch (e) {
    // ignore
  }
}

function showHuddleBrowserNotification(inviterUserId, groupId) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  try {
    const allUsers = store.getters["users/users"] || [];
    const inviter = allUsers.find((u) => Number(u.id) === Number(inviterUserId));
    const inviterName = inviter
      ? `${inviter.first_name || ""} ${inviter.last_name || ""}`.trim() || inviter.name || "Someone"
      : "Someone";

    const groups = store.getters["chats/groups"] || [];
    const group = groups.find((g) => String(g.id) === String(groupId));
    const groupName = group?.name || "a huddle";

    const notification = new Notification(`${inviterName} is calling`, {
      body: `Huddle in ${groupName}`,
      icon: inviter?.profile_pic || inviter?.logo_url || undefined,
      tag: `huddle-invite-${groupId}`,
      requireInteraction: true,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto-close after 30s
    setTimeout(() => notification.close(), 30000);
  } catch (error) {
    console.error("Failed to show huddle browser notification:", error);
  }
}

function handleHuddleStarted(event) {
  if (!localStorage.getItem("huddle")) return;
  console.log("[Huddle] HUDDLE_STARTED event received:", event);

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isAlreadyInHuddle = store.getters["huddles/isConnected"];

  // Only show invite if current user is a member of this group
  if (!isUserGroupMember(event.group_id, currentUserId)) return;

  store.dispatch("huddles/handleHuddleStarted", event);

  if (event.created_by && Number(event.created_by) !== Number(currentUserId) && !isAlreadyInHuddle) {
    // Skip if we already have a pending invite for this session (from handleHuddleInvite)
    const existingInvite = store.getters["huddles/incomingInvite"];
    if (existingInvite && String(existingInvite.session_id) === String(event.session_id)) return;

    store.dispatch("huddles/setIncomingInvite", {
      session_id: event.session_id,
      group_id: event.group_id,
      inviter_user_id: event.created_by,
      participants: event.participants || [],
    });

    playCallingSound();
    showHuddleBrowserNotification(event.created_by, event.group_id);
  }
}

function handleHuddleEnded(event) {
  const wasInHuddle = store.getters["huddles/isConnected"];
  const activeHuddle = store.getters["huddles/activeHuddle"];
  const matchesActiveHuddle = activeHuddle && String(activeHuddle.session_id) === String(event.session_id);

  // Stop calling sound and browser notification if we had a pending invite for this huddle
  const incomingInvite = store.getters["huddles/incomingInvite"];
  if (incomingInvite && String(incomingInvite.session_id) === String(event.session_id)) {
    stopCallingSound();
    closeHuddleBrowserNotification(event.group_id);
  }

  store.dispatch("huddles/handleHuddleEnded", event);

  if (wasInHuddle && matchesActiveHuddle) {
    playCallEndSound();
  }
}

function handleHuddleJoined(event) {
  store.dispatch("huddles/handleParticipantJoined", event);

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isInHuddle = store.getters["huddles/isConnected"];
  if (isInHuddle && event.user_id && Number(event.user_id) !== Number(currentUserId)) {
    playMessageNotification();
  }
}

function handleHuddleLeft(event) {
  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isInHuddle = store.getters["huddles/isConnected"];

  store.dispatch("huddles/handleParticipantLeft", event);

  if (isInHuddle && event.user_id && Number(event.user_id) !== Number(currentUserId)) {
    playCallEndSound();
  }
}

function handleHuddleMuted(event) {
  store.dispatch("huddles/handleParticipantMuted", event);
}

function handleHuddleActiveSpeaker(event) {
  // When connected to LiveKit, RoomEvent.ActiveSpeakersChanged is authoritative —
  // skip MQTT events to avoid racing with LiveKit's real-time speaker updates.
  const isConnected = store.getters["huddles/isConnected"];
  if (isConnected) return;

  // For non-connected users viewing huddle state, use the MQTT event directly
  store.dispatch("huddles/setActiveSpeakers", [String(event.user_id)]);
}

async function handleHuddleInvite(event) {
  if (!localStorage.getItem("huddle")) return;
  console.log("[Huddle] HUDDLE_INVITE event received:", event);
  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  if (!currentUserId) return;

  // Only process if current user is a member of this group
  if (!isUserGroupMember(event.group_id, currentUserId)) return;

  // Check user_ids array, invitee_user_id, and invited_user_id fields
  const invitedUserIds = (event.user_ids || []).map((id) => String(id));
  const isDirectlyInvited =
    (event.invitee_user_id && String(event.invitee_user_id) === String(currentUserId)) ||
    (event.invited_user_id && String(event.invited_user_id) === String(currentUserId));
  if (!invitedUserIds.includes(String(currentUserId)) && !isDirectlyInvited) return;

  const isAlreadyInHuddle = store.getters["huddles/isConnected"];
  if (isAlreadyInHuddle) return;

  // Skip if we already have a pending invite for this session (from handleHuddleStarted)
  const existingInvite = store.getters["huddles/incomingInvite"];
  if (existingInvite && String(existingInvite.session_id) === String(event.session_id)) return;

  store.dispatch("huddles/setIncomingInvite", {
    session_id: event.session_id,
    group_id: event.group_id,
    inviter_user_id: event.inviter_user_id,
  });

  playCallingSound();
  showHuddleBrowserNotification(event.inviter_user_id, event.group_id);
  eventBus.emit("huddle-invite-received", event);
}

function handlePresenceChanged(event) {
  const { user_id, is_online, last_seen } = event;
  if (!user_id) return;
  store.commit("users/SET_USER_PRESENCE", {
    userId: user_id,
    is_online: !!is_online,
    last_seen: last_seen || null,
  });
}

const _embeddedSeenMessageIds = new Set();

async function handleEmbeddedChatNewMessage(event) {
  const { group_id, message_id, sender_id, thread_id } = event;
  if (!group_id || !message_id) return;

  if (_embeddedSeenMessageIds.has(message_id)) return;
  _embeddedSeenMessageIds.add(message_id);
  setTimeout(() => _embeddedSeenMessageIds.delete(message_id), 10000);

  const openChatIds = store.getters["embeddedChats/openChatIds"] || [];
  const eventGroupId = String(group_id).replace(/^group_/, '');

  if (!openChatIds.some((id) => String(id).replace(/^group_/, '') === eventGroupId)) {
    return;
  }

  const currentUserId = store.getters["auth/userData"]?.id || localStorage.getItem("userId");
  const isOwnEvent = sender_id && currentUserId &&
    (Number(sender_id) === Number(currentUserId) || String(sender_id) === String(currentUserId));

  // Skip own messages early — only fetch if it might be a broadcast copy (has thread_id)
  if (isOwnEvent && !thread_id) return;

  try {
    const response = await ChatService.fetchMessageById(message_id, group_id);
    if (response?.success && response?.message) {
      // For own messages that made it here (had thread_id), only allow broadcast copies
      if (isOwnEvent && !response.message.broadcast_from_thread_id) return;

      const matchedChatId = openChatIds.find(
        (id) => String(id).replace(/^group_/, '') === eventGroupId
      );
      if (matchedChatId) {
        store.commit("embeddedChats/ADD_MESSAGE", {
          chatId: matchedChatId,
          message: response.message,
        });
      }
    }
  } catch (err) {
    console.error("Failed to fetch embedded chat message:", err);
  }
}

export function registerEventHandlers() {
  if (eventManager.handlers.size > 0) return;
  eventManager.registerHandler(EventTypes.NEW_MESSAGE, handleNewMessage);
  eventManager.registerHandler(EventTypes.NEW_MESSAGE, handleEmbeddedChatNewMessage);
  eventManager.registerHandler(EventTypes.MESSAGE_EDITED, handleMessageEdited);
  eventManager.registerHandler(EventTypes.MESSAGE_DELETED, handleMessageDeleted);
  eventManager.registerHandler(EventTypes.REACTION_UPDATED, handleReactionUpdated);
  eventManager.registerHandler(EventTypes.THREAD_COUNT_UPDATED, handleThreadCountUpdated);
  eventManager.registerHandler(EventTypes.NEW_THREAD_REPLY, handleNewThreadReply);
  eventManager.registerHandler(EventTypes.THREAD_REPLY_EDITED, handleThreadReplyEdited);
  eventManager.registerHandler(EventTypes.THREAD_REPLY_DELETED, handleThreadReplyDeleted);
  eventManager.registerHandler(EventTypes.THREAD_REACTION_UPDATED, handleThreadReactionUpdated);
  eventManager.registerHandler(EventTypes.TYPING, handleTyping);
  eventManager.registerHandler(EventTypes.SIDEBAR_NEW_MESSAGE, handleSidebarNewMessage);
  eventManager.registerHandler(EventTypes.SIDEBAR_NEW_MESSAGE, handleEmbeddedChatNewMessage);
  eventManager.registerHandler(EventTypes.GROUP_UPDATED, handleGroupUpdated);
  eventManager.registerHandler(EventTypes.ADDED_TO_GROUP, handleAddedToGroup);
  eventManager.registerHandler(EventTypes.REMOVED_FROM_GROUP, handleRemovedFromGroup);
  eventManager.registerHandler(EventTypes.UNREAD_UPDATE, handleUnreadUpdate);
  eventManager.registerHandler(EventTypes.HUDDLE_STARTED, handleHuddleStarted);
  eventManager.registerHandler(EventTypes.HUDDLE_ENDED, handleHuddleEnded);
  eventManager.registerHandler(EventTypes.HUDDLE_JOINED, handleHuddleJoined);
  eventManager.registerHandler(EventTypes.HUDDLE_LEFT, handleHuddleLeft);
  eventManager.registerHandler(EventTypes.HUDDLE_MUTED, handleHuddleMuted);
  eventManager.registerHandler(EventTypes.HUDDLE_ACTIVE_SPEAKER, handleHuddleActiveSpeaker);
  eventManager.registerHandler(EventTypes.HUDDLE_INVITE, handleHuddleInvite);
  eventManager.registerHandler(EventTypes.PRESENCE_CHANGED, handlePresenceChanged);
  eventBus.on("chat-sidebar-event", handleChatSidebarEvent);
}

export function unregisterEventHandlers() {
  eventManager.clear();
  eventBus.off("chat-sidebar-event", handleChatSidebarEvent);
}
